import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { SignalRConnectionStatus, MasaSiparisi, MasaSiparisDetay, TrendyolOrder } from '../types';
import { mainApi, getirApi, migrosApi, yemeksepetiApi } from '../../../../config/apiClient';
import { getBusinessDayInfo, isBusinessDayDate } from '../utils/helpers';
import { mapSqlResponseToTrendyolOrder } from '../services/TrendyolYemekService';
import { infiniteRetryPolicy, startResilientConnection } from '../../../../utils/signalRConnection';
import { isMigrosNewOrderNotifiableStatus } from '../utils/migrosStatus';
import { isYsNewOrderNotifiableStatus } from '../utils/yemeksepetiDelivery';
import { mapMigrosOrderToMasaSiparisi, mapYsOrderToMasaSiparisi } from '../utils/mapPlatformOrderToMasaSiparisi';
import {
  migrosNewOrderNotificationKey,
  platformNewOrderNotificationKey,
  ysNewOrderNotificationKey,
} from '../utils/platformNotificationKey';
import {
  applyNewOrderSafetyNet,
  markSafetyNetSeen,
  resetNewOrderSafetyNet,
  safetyNetOrderId,
} from '../utils/platformNewOrderSafetyNet';
import type { MasaOrderSource } from '../utils/masaSiparisiNotification';
import {
  isMasaNewOrderNotifiableStatus,
  masaNewOrderNotificationKey,
} from '../utils/masaSiparisiNotification';

// ============================================================================
// USE SIGNALR HOOK - QR MENU / E-TİCARET / KIOSK SİPARİŞLERİ
// ============================================================================

export interface GetirCancelInfo {
  getirId: string;
  confirmationId?: string;
  status: number;
  cancelReasonText: string;
  clientName?: string;
  cancelledAt: string;
}

export interface MigrosCancelInfo {
  migrosOrderId: number;
  dbId: number;
  status: string;
  cancelReasonId?: number;
  customerName?: string;
  cancelledAt: string;
}

export interface MigrosDeliveryStatusInfo {
  migrosOrderId: number;
  dbId: number;
  deliveryStatus: string;
  courierName?: string;
  courierPhone?: string;
  estimatedDeliveryTime?: string;
}

// ============================================================================
// MIGROS SIPARIS TIPLERI
// ============================================================================
export interface MigrosOrderItem {
  id: number;
  itemId: number;
  productId: number;
  name: string;
  amount: number;
  unitPrice: number;
  price: number;
  note?: string;
  options?: MigrosOrderItemOption[];
}

export interface MigrosOrderItemOption {
  id: number;
  objectOptionItemId: number;
  optionItemId: number;
  productId: number;
  parentObjectOptionItemId: number;
  headerName: string;
  itemNames: string;
  primaryPrice: number;
  primaryDiscountedPrice: number;
  quantity: number;
  excluded: boolean;
  optionType: string;
}

export interface MigrosOrder {
  id: number;
  migrosOrderId: number;
  description: string;
  status: string;
  deliveryProvider: string;
  storeId: number;
  storeName: string;
  storeGroupId: number;
  storeGroupName: string;
  customerId: number;
  customerFirstName: string;
  customerLastName: string;
  customerFullName: string;
  customerPhoneNumber: string;
  addressId: number;
  addressDirection: string;
  addressDetail: string;
  addressStreetName: string;
  addressBuildingNumber: string;
  addressFloorNumber: string;
  addressDoorNumber: string;
  cityName: string;
  townName: string;
  districtName: string;
  latitude?: number;
  longitude?: number;
  totalPrice: number;
  discountedPrice: number;
  restaurantDiscountedPrice: number;
  migrosDiscountedPrice: number;
  paymentType: string;
  paymentDescription: string;
  isOnlinePayment: boolean;
  orderNote: string;
  saveGreen: boolean;
  contactlessDelivery: boolean;
  ringDoorBell: boolean;
  shortCode: string;
  createdAt: string;
  createdAsMs: number;
  receivedAt: string;
  updatedAt?: string;
  estimatedDeliveryTime?: number;
  rawJsonData?: string;
  companyId?: string;
  branchId?: string;
  assignedCourierId?: number;
  assignedCourierName?: string;
  assignedCourierFirebaseUid?: string;
  paymentStatus?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  paidAmount?: number;
  items?: MigrosOrderItem[];
  // Çoklu mağaza (Faz 3 damgası). storeName Migros'un kendi mağaza adıdır;
  // kullanıcının girdiği marka adı integrationStoreName alanından gelir.
  platformIntegrationId?: number;
  integrationStoreName?: string;
}

// ============================================================================
// YEMEKSEPETI SIPARIS TIPLERI
// ============================================================================
export interface YemeksepetiOrderItem {
  id: number;
  orderId: number;
  productId?: string;
  remoteCode?: string;
  categoryName?: string;
  name: string;
  quantity: number;
  unitPrice: number;
  paidPrice: number;
  comment?: string;
  variationName?: string;
  toppingsJson?: string;
  // Yemeksepeti v2026 (15.05.2026): backend yeni nullable kolonlari.
  // Eski siparislerde null gelir; yeni siparislerde DiscountWithSponsorship[]
  // serialize edilmis JSON gelir.
  discountsJson?: string | null;
  // Indirim oncesi orijinal birim fiyat. Yoksa unitPrice ile fallback yapilir.
  originalUnitPrice?: number | null;
}

export interface YemeksepetiOrder {
  id: number;
  token: string;
  code: string;
  remoteOrderId: string;
  remoteId?: string;
  shortCode?: string;
  status: string;
  statusMessage?: string;
  orderType?: string;
  expeditionType?: string;
  isTest: boolean;
  isPreOrder: boolean;
  customerFirstName?: string;
  customerLastName?: string;
  customerFullName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerComment?: string;
  deliveryAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryCity?: string;
  deliveryDistrict?: string;
  expectedDeliveryTime?: string;
  riderPickupTime?: string;
  expressDelivery: boolean;
  paymentStatus?: string;
  paymentType?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  paidAmount?: number;
  grandTotal: number;
  payRestaurant: number;
  riderTip: number;
  totalNet: number;
  vatTotal: number;
  collectFromCustomer: number;
  platform?: string;
  platformKey?: string;
  platformRestaurantId?: string;
  callbackAcceptUrl?: string;
  callbackRejectUrl?: string;
  callbackPreparedUrl?: string;
  callbackPickedUpUrl?: string;
  rawJsonData?: string;
  // Yemeksepeti v2026 (15.05.2026): order-level indirimler (sponsorship dahil)
  // backend tarafinda JSON serialize edilmis kolon. Eski siparislerde null gelir.
  orderDiscountsJson?: string | null;
  companyId?: string;
  branchId?: string;
  assignedCourierId?: number;
  assignedCourierName?: string;
  assignedCourierFirebaseUid?: string;
  createdAt: string;
  expiryDate?: string;
  receivedAt: string;
  processedAt?: string;
  completedAt?: string;
  statusUpdatedAt?: string;
  updatedAt?: string;
  items?: YemeksepetiOrderItem[];
  // Çoklu mağaza (Faz 3 damgası)
  platformIntegrationId?: number;
  storeName?: string;
}

export interface YemeksepetiCancelInfo {
  orderId: number;
  status: string;
  customerName?: string;
  cancelledAt: string;
  /**
   * SignalR backend'inin gönderdiği **tam YS sipariş objesi** (`orderData`).
   * Modal bu objeden sipariş no, telefon, adres, toplam tutar, ham JSON ve
   * iptal sebebi gibi alanları okur. Polling fallback'inde (listeden bulunan
   * `yemeksepetiOrders[i]`) aynı yapı paslanır.
   *
   * Eski sürümlerde `undefined` gelebilir → modal güvenli fallback'le çalışır.
   */
  rawOrder?: any;
}

export interface TrendyolOrderUpdatedInfo {
  order: TrendyolOrder;
  isNew: boolean;
  statusChanged: boolean;
  /** platform|sipariş|new-order. processedOrdersRef ve kuyruk aynı dizeyi kullanır. */
  notificationKey?: string;
  /** 30 sn içinde aynı yeni sipariş ikinci kez kuyruğa girmez. Durum güncellemesi yine uygulanır. */
  duplicateNewOrder?: boolean;
}

export interface UseSignalROptions {
  companyId: string | null;
  branchId?: string | null;
  onNewOrder?: (order: MasaSiparisi) => void;
  onOrderUpdated?: (order: MasaSiparisi) => void;
  onOrderDeleted?: (orderId: number) => void;
  onGetirOrderCancelled?: (cancelInfo: GetirCancelInfo) => void;
  onGetirTokenError?: () => void;
  onMigrosOrderCancelled?: (cancelInfo: MigrosCancelInfo) => void;
  onMigrosDeliveryStatusChanged?: (statusInfo: MigrosDeliveryStatusInfo) => void;
  onYemeksepetiOrderCancelled?: (cancelInfo: YemeksepetiCancelInfo) => void;
  /**
   * Trendyol server-side polling modunda local-service → backend → SignalR push
   * akışıyla gelen sipariş güncellemeleri. PackageOrderScreen bunu yakalayıp
   * `useTrendyolYemek.setTrendyolOrders`'ı upsert eder. Eski mod'da push hiç gelmez
   * → handler zaten dinlemez → zararsız no-op.
   */
  onTrendyolOrderUpdated?: (info: TrendyolOrderUpdatedInfo) => void;
}

// ============================================================================
// GETİR SİPARİŞ TİPLERİ
// ============================================================================
export interface GetirOrderItem {
  id: number;
  name: string;
  quantity: number;
  price: number;
  totalPrice: number;
  note?: string;
  imageUrl?: string;
}

export interface GetirOrder {
  id: number;
  getirId: string;
  confirmationId: string;
  status: number;
  statusText: string;
  companyId: string;
  branchId?: string;
  clientName?: string;
  clientPhone?: string;
  contactPhone?: string;
  deliveryAddress?: string;
  city?: string;
  district?: string;
  clientNote?: string;
  totalPrice: number;
  totalDiscountedPrice?: number;
  deliveryType: number;
  deliveryTypeName?: string;
  paymentMethod?: number;
  paymentMethodText?: string;
  isScheduled?: boolean;
  scheduledDate?: string;
  doNotKnock?: boolean;
  dropOffAtDoor?: boolean;
  isEcoFriendly?: boolean;
  checkoutDate?: string;
  verifyDate?: string;
  prepareDate?: string;
  handoverDate?: string;
  deliverDate?: string;
  createdAt: string;
  updatedAt?: string;
  courierName?: string;
  courierStatus?: number;
  restaurantId?: string;
  restaurantName?: string;
  itemCount?: number;
  items?: GetirOrderItem[];
  // Restoran tarafından atanan kurye bilgileri
  assignedCourierId?: number;
  assignedCourierName?: string;
  assignedCourierFirebaseUid?: string;
  // Ödeme bilgileri (kapıda ödeme yapılan siparişler için)
  paymentStatus?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  paidAmount?: number;
  paidBy?: string;
  // Çoklu mağaza (Faz 3 damgası). restaurantName Getir'in verdiği addır;
  // storeName kullanıcının girdiği marka adıdır.
  platformIntegrationId?: number;
  storeName?: string;
}

/**
 * Marka bazli restoran acik/kapali durumu. Anahtar PlatformIntegrations.Id'dir;
 * entegrasyon kimligi gonderilmeden yapilan cagrilar DEFAULT_STORE_KEY altinda
 * toplanir (tek magazali kurulumlarin bugunku davranisi).
 */
export type StoreOpenStateMap = Record<number, boolean>;

export const DEFAULT_STORE_KEY = 0;

/** Haritada kapali kayit yoksa acik sayilir; bos harita da acik demektir. */
export const isEveryStoreOpen = (map: StoreOpenStateMap): boolean =>
  Object.values(map).every(value => value !== false);

/**
 * Migros durum guncelleme sonucu. `success: false` her zaman hata anlamina gelmez;
 * asagidaki bayraklardan biri doluysa backend siparisin Migros'taki gercek durumunu
 * bildirmis ve yerel liste buna gore esitlenmistir.
 */
export interface MigrosStatusUpdateResult {
  success: boolean;
  message?: string;
  /** Siparis Migros tarafindan iptal edilmis. */
  alreadyCancelled?: boolean;
  /** Backend, DB durumunu Migros'taki degerle esitledi. */
  selfHealed?: boolean;
  /** Migros'ta beklenenden farkli bir durumda. */
  statusMismatch?: boolean;
  /** Siparis Migros'ta zaten bu durumda; istek tekrarlanmis. */
  alreadyInStatus?: boolean;
  /** Ayni siparis icin bir istek halen suruyor; bu cagri gonderilmedi. */
  inFlight?: boolean;
  /** Migros'un bildirdigi ham durum (okuma sozlugu: COLLECTING, IN_DELIVERY...). */
  migrosStatus?: string;
}

export interface UseSignalRReturn {
  status: SignalRConnectionStatus;
  qrMenuOrders: MasaSiparisi[];
  ecommerceOrders: MasaSiparisi[];
  kioskOrders: MasaSiparisi[];
  getirOrders: GetirOrder[];
  migrosOrders: MigrosOrder[];
  yemeksepetiOrders: YemeksepetiOrder[];
  setQrMenuOrders: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  setEcommerceOrders: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  setKioskOrders: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  setGetirOrders: React.Dispatch<React.SetStateAction<GetirOrder[]>>;
  setMigrosOrders: React.Dispatch<React.SetStateAction<MigrosOrder[]>>;
  setYemeksepetiOrders: React.Dispatch<React.SetStateAction<YemeksepetiOrder[]>>;
  allowedQrMenuIdsLoaded: boolean;
  isQrOrderForThisBranch: (order: MasaSiparisi) => boolean;
  loadAllowedQrMenuIds: () => Promise<void>;
  fetchMasaSiparisleri: () => Promise<void>;
  fetchGetirOrders: () => Promise<void>;
  fetchMigrosOrders: () => Promise<void>;
  fetchYemeksepetiOrders: () => Promise<void>;
  updateMasaSiparisiStatus: (orderId: number, newStatus: string) => Promise<boolean>;
  updateGetirOrderStatus: (orderId: number, newStatus: string) => Promise<{ success: boolean; message?: string }>;
  updateMigrosOrderStatus: (orderId: number, newStatus: string, cancelReasonId?: number) => Promise<MigrosStatusUpdateResult>;
  updateYemeksepetiOrderStatus: (orderId: number, action: string, reason?: string) => Promise<{ success: boolean; message?: string }>;
  syncGetirOrderStatuses: () => Promise<{ updatedCount: number }>;
  pollMigrosOrders: () => Promise<void>;
  /** Toplu gosterge: markalarin hepsi acikken true. */
  getirRestaurantOpen: boolean;
  migrosRestaurantOpen: boolean;
  yemeksepetiVendorOpen: boolean;
  /** Marka bazli durum. Sekme rozeti 'kismen acik' durumunu buradan ayirt eder. */
  getirRestaurantOpenMap: StoreOpenStateMap;
  migrosRestaurantOpenMap: StoreOpenStateMap;
  yemeksepetiVendorOpenMap: StoreOpenStateMap;
  checkGetirRestaurantStatus: (integrationId?: number) => Promise<void>;
  checkMigrosRestaurantStatus: (integrationId?: number) => Promise<void>;
  checkYemeksepetiVendorStatus: (integrationId?: number) => Promise<void>;
  openGetirRestaurant: (integrationId?: number) => Promise<{ success: boolean; message?: string }>;
  closeGetirRestaurant: (integrationId?: number) => Promise<boolean>;
  openMigrosRestaurant: (integrationId?: number) => Promise<boolean>;
  closeMigrosRestaurant: (integrationId?: number) => Promise<boolean>;
  openYemeksepetiVendor: (integrationId?: number) => Promise<boolean>;
  closeYemeksepetiVendor: (closedReason?: string, integrationId?: number) => Promise<boolean>;
}

/**
 * SignalR bağlantı hook'u - QR Menü, E-Ticaret ve Kiosk siparişlerini yönetir
 */
export const useSignalR = (options: UseSignalROptions): UseSignalRReturn => {
  const { companyId, branchId, onNewOrder, onOrderUpdated, onOrderDeleted, onGetirOrderCancelled, onGetirTokenError, onMigrosOrderCancelled, onMigrosDeliveryStatusChanged, onYemeksepetiOrderCancelled, onTrendyolOrderUpdated } = options;

  // onTrendyolOrderUpdated ref - dependency invalidation'ı önlemek için
  // (callback referansı her render'da değişse bile SignalR connection'ı yeniden kurulmasın)
  const onTrendyolOrderUpdatedRef = useRef(onTrendyolOrderUpdated);
  onTrendyolOrderUpdatedRef.current = onTrendyolOrderUpdated;
  const onNewOrderRef = useRef(onNewOrder);
  onNewOrderRef.current = onNewOrder;

  // branchId ref - SignalR push handler'larında stale closure'ı önlemek için
  // (örn. ilk mount'ta branchId=null ile setup edilen connection, sonradan branchId değişse bile
  // push geldiğinde en güncel branchId ile fetch yapmalı; aksi halde branchId'siz fetch tüm
  // şirketin verilerini çekip canlı state'i diğer şubelerle kirletir)
  const branchIdRef = useRef(branchId);
  branchIdRef.current = branchId;

  // Fetcher ref'leri - push handler'lar stale fetcher referansı kullanmasın diye
  // (SignalR connection bir kez kurulduğunda push handler içindeki fetchXxx() çağrısı
  //  o anki branchId closure'ını yakalar; eski connection cleanup'tan önce push gelirse
  //  branchId'siz fetch yapılır. Bu ref'ler her zaman en güncel fetcher'a işaret eder.)
  const fetchGetirOrdersRef = useRef<(() => Promise<void>) | null>(null);
  const fetchMigrosOrdersRef = useRef<(() => Promise<void>) | null>(null);
  const fetchYemeksepetiOrdersRef = useRef<(() => Promise<void>) | null>(null);

  // SQL emniyet ağı: ilk fetch (ekran açılışı) sessiz tohumlar; sonraki
  // fetch'lerde (1 dk yoklama, reconnect) kaçırılmış yeni sipariş için onNewOrder.
  const migrosSafetySeenRef = useRef<Set<string>>(new Set());
  const migrosSafetySeededRef = useRef(false);
  const ysSafetySeenRef = useRef<Set<string>>(new Set());
  const ysSafetySeededRef = useRef(false);
  // QR Menü + Kiosk: push hem hızlı yoldan hem outbox worker'ından gelebilir, ikisi de
  // kaçarsa (tarayıcı kapalı/kopuk) sipariş yalnızca yoklamayla listeye düşer.
  const masaSafetySeenRef = useRef<Set<string>>(new Set());
  const masaSafetySeededRef = useRef(false);

  // SignalR bağlantı durumu
  const [status, setStatus] = useState<SignalRConnectionStatus>('Disconnected');
  
  // QR Menü, E-Ticaret, Kiosk, Getir siparişleri
  const [qrMenuOrders, setQrMenuOrders] = useState<MasaSiparisi[]>([]);
  const [ecommerceOrders, setEcommerceOrders] = useState<MasaSiparisi[]>([]);
  const [kioskOrders, setKioskOrders] = useState<MasaSiparisi[]>([]);
  const [getirOrders, setGetirOrders] = useState<GetirOrder[]>([]);
  const [migrosOrders, setMigrosOrders] = useState<MigrosOrder[]>([]);
  const [yemeksepetiOrders, setYemeksepetiOrders] = useState<YemeksepetiOrder[]>([]);
  
  // Restoran acik/kapali durumlari — marka (entegrasyon) bazli.
  // Anahtar: PlatformIntegrations.Id; entegrasyon kimligi verilmeyen eski
  // cagrilar DEFAULT_STORE_KEY altina yazar.
  const [getirRestaurantOpenMap, setGetirRestaurantOpenMap] = useState<StoreOpenStateMap>({});
  const [migrosRestaurantOpenMap, setMigrosRestaurantOpenMap] = useState<StoreOpenStateMap>({});
  const [yemeksepetiVendorOpenMap, setYemeksepetiVendorOpenMap] = useState<StoreOpenStateMap>({});

  // Eski tuketiciler icin toplu gosterge: hicbiri kapali degilse acik sayilir.
  const getirRestaurantOpen = isEveryStoreOpen(getirRestaurantOpenMap);
  const migrosRestaurantOpen = isEveryStoreOpen(migrosRestaurantOpenMap);
  const yemeksepetiVendorOpen = isEveryStoreOpen(yemeksepetiVendorOpenMap);
  
  // Duplicate SignalR event kontrolü
  const processedOrdersRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    resetNewOrderSafetyNet(migrosSafetySeenRef.current, migrosSafetySeededRef);
    resetNewOrderSafetyNet(ysSafetySeenRef.current, ysSafetySeededRef);
    resetNewOrderSafetyNet(masaSafetySeenRef.current, masaSafetySeededRef);
  }, [companyId, branchId]);
  
  // Şubeye ait QR Menü ID'leri (sadece bu şubenin menülerinden gelen siparişler gösterilir)
  const allowedQrMenuIdsRef = useRef<Set<string>>(new Set());
  const [allowedQrMenuIdsLoaded, setAllowedQrMenuIdsLoaded] = useState(false);
  
  // ============================================================================
  // YARDIMCI FONKSİYONLAR
  // ============================================================================
  
  /**
   * MasaSiparisi'nden kaynak tipini belirle
   */
  const getOrderSource = useCallback((siparis: MasaSiparisi): 'qr' | 'ecommerce' | 'kiosk' | null => {
    const kaynak = siparis.siparisKaynagi?.toLowerCase() || '';
    
    // Restoran / Hızlı Satış siparişleri paket ekranına ait değil
    if (kaynak === 'restaurantquicksale' || kaynak === 'quicksale') {
      return null;
    }
    if (siparis.siparisId?.startsWith('RS-') || siparis.siparisId?.includes('-HS-')) {
      return null;
    }
    
    if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce')) {
      return 'ecommerce';
    } else if (kaynak.includes('kiosk')) {
      return 'kiosk';
    } else {
      return 'qr';
    }
  }, []);
  
  /**
   * QR menü siparişinin bu ekranın şubesine ait olup olmadığını kontrol et
   * Mantık:
   * 1. Ekrana şube tanımlı DEĞİLSE → tüm siparişler gösterilir
   * 2. Ekrana şube tanımlıysa ve QRMenuDefinitions'da eşleşen menü VARSA → sadece o menülerden gelenler
   * 3. Ekrana şube tanımlıysa AMA QRMenuDefinitions'da eşleşen menü YOKSA → HİÇBİR QR sipariş gösterilmez
   */
  const isQrOrderForThisBranch = useCallback((order: MasaSiparisi): boolean => {
    // Eğer ekrana şube tanımlı değilse, tüm siparişleri göster
    if (!branchId) {
      return true;
    }
    
    // Doğrudan branchId karşılaştırması (en güvenilir yöntem)
    if (order.branchId) {
      const orderBranchId = String(order.branchId);
      const currentBranchId = String(branchId);
      if (orderBranchId !== currentBranchId) {
        console.log(`⏭️ [SignalR] Sipariş şube filtresi (branchId): ${order.siparisId} - orderBranch: ${orderBranchId} - currentBranch: ${currentBranchId} - BU ŞUBEYE AİT DEĞİL`);
        return false;
      }
      return true;
    }
    
    // branchId yoksa (eski veri), QRMenuId üzerinden kontrol et
    if (!allowedQrMenuIdsLoaded) {
      return true;
    }
    
    if (allowedQrMenuIdsRef.current.has('__NONE__')) {
      console.log(`⏭️ [SignalR] Şube ${branchId} için QR menü tanımlı değil, QR siparişleri gösterilmiyor`);
      return false;
    }
    
    if (!order.qrMenuId) {
      return true;
    }
    
    const isAllowed = allowedQrMenuIdsRef.current.has(String(order.qrMenuId));
    
    if (!isAllowed) {
      console.log(`⏭️ [SignalR] Sipariş şube filtresi: ${order.siparisId} - QRMenuId: ${order.qrMenuId} - BU ŞUBEYE AİT DEĞİL`);
    }
    
    return isAllowed;
  }, [branchId, allowedQrMenuIdsLoaded]);
  
  /**
   * Şubeye ait QR Menü ID'lerini yükle
   */
  const loadAllowedQrMenuIds = useCallback(async () => {
    // Eğer şube tanımlı değilse, filtreleme yapma (tüm siparişler gösterilir)
    if (!branchId) {
      console.log('📋 [SignalR] Şube tanımlı değil, tüm QR menü siparişleri gösterilecek');
      allowedQrMenuIdsRef.current = new Set();
      setAllowedQrMenuIdsLoaded(true);
      return;
    }
    
    // Zaten yüklendiyse tekrar yükleme
    if (allowedQrMenuIdsLoaded) {
      return;
    }
    
    try {
      const { data: result } = await mainApi.get(`/api/QRMenu/branch/${branchId}/menu-ids`);
      console.log('🔍 [SignalR] QR Menu API yanıtı:', result);
      
      // API küçük harfle "success" ve "data" döndürüyor
      const isSuccess = result.Success || result.success;
      const data = result.Data || result.data;
      
      if (isSuccess && data && data.length > 0) {
        const menuIds = new Set<string>(data);
        allowedQrMenuIdsRef.current = menuIds;
        setAllowedQrMenuIdsLoaded(true);
        console.log(`✅ [SignalR] Şube ${branchId} için ${menuIds.size} QR menü ID yüklendi:`, Array.from(menuIds));
      } else if (isSuccess && (!data || data.length === 0)) {
        // API başarılı ama boş döndü - bu şubeye ait QR menü yok
        console.warn(`⚠️ [SignalR] Şube ${branchId} için QR menü bulunamadı, QR siparişleri GÖSTERİLMEYECEK`);
        allowedQrMenuIdsRef.current = new Set(['__NONE__']);
        setAllowedQrMenuIdsLoaded(true);
      } else {
        console.error('❌ [SignalR] API başarısız:', result);
        allowedQrMenuIdsRef.current = new Set();
        setAllowedQrMenuIdsLoaded(true);
      }
    } catch (error: any) {
      console.error('❌ [SignalR] QR menü ID\'leri yüklenirken hata:', error);
      allowedQrMenuIdsRef.current = new Set();
      setAllowedQrMenuIdsLoaded(true);
    }
  }, [companyId, branchId, allowedQrMenuIdsLoaded]);
  
  /**
   * MasaSiparisleri'ni API'den yükle (başlangıçta)
   */
  const fetchMasaSiparisleri = useCallback(async () => {
    if (!companyId) return;
    const t0 = performance.now();
    try {
      // ✅ branchId backend'de filtrelenir: ekrana şube tanımlıysa o şubenin
      // siparişlerini (ve henüz BranchId atanmamış ama QRMenu.BranchId eşleşen
      // siparişleri) yalnızca SQL düzeyinde çekeriz. Şirket bazlı çekip
      // frontend'de elemek (Hacıoğlu gibi 58 şubeli) 5+ sn gereksiz veri demek.
      const { data: result } = await mainApi.get(`/api/MasaSiparisi/orders`, {
        params: branchId ? { branchId } : undefined,
      });
      if (result.success && result.data) {
          const allOrders: MasaSiparisi[] = result.data.map((order: any) => {
            // Sipariş detaylarını parse et
            let siparisDetaylari: MasaSiparisDetay[] = [];
            try {
              if (typeof order.siparisDetaylari === 'string') {
                const parsed = JSON.parse(order.siparisDetaylari || '[]');
                siparisDetaylari = parsed.siparisDetaylari || parsed || [];
              } else if (order.siparisDetaylari) {
                siparisDetaylari = order.siparisDetaylari.siparisDetaylari || order.siparisDetaylari || [];
              }
            } catch (e) {
              console.error('Sipariş detayları parse hatası:', e);
            }
            
            return {
              id: order.id,
              siparisId: order.siparisId,
              masaNumarasi: order.masaNumarasi || order.masaId,
              masaAdi: order.masaAdi,
              kullaniciAdi: order.kullaniciAdi,
              siparisDurumu: order.siparisDurumu,
              siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
              siparisGuncellenmeTarihi: order.siparisGuncellenmeTarihi,
              toplamTutar: order.toplamTutar,
              qrMenuId: order.qrMenuId,
              branchId: order.branchId,
              siparisDetaylari: siparisDetaylari,
              siparisNotu: order.siparisNotu,
              siparisKaynagi: order.siparisKaynagi,
              musteriAdi: order.musteriAdi || order.kullaniciAdi,
              musteriTelefon: order.musteriTelefon,
              musteriAdres: order.musteriAdres,
            };
          });
          
          // Bugünün (ve gece modunda dünün) siparişlerini filtrele
          const todayOrders = allOrders.filter(order => {
            const orderDate = order.siparisOlusturmaTarihi?.split('T')[0];
            if (!orderDate || !isBusinessDayDate(orderDate)) return false;
            
            // İptal olanları hariç tut
            const durum = order.siparisDurumu?.toLowerCase() || '';
            if (durum === 'iptal' || durum === 'cancelled') return false;
            
            // ✅ Hızlı Satış siparişlerini hariç tut (HS siparişleri paket ekranına düşmemeli)
            if (order.siparisId?.includes('-HS-')) return false;
            
            // ✅ Restoran siparişlerini hariç tut (RS siparişleri restoran ekranına ait)
            if (order.siparisId?.startsWith('RS-')) return false;
            
            // ✅ SyncSource/siparisKaynagi ile restoran siparişlerini hariç tut
            const kaynak = (order.siparisKaynagi || '').toLowerCase();
            if (kaynak === 'restaurantquicksale') return false;
            
            return true;
          });
          
          // Kaynaklara göre ayır
          // ✅ siparisKaynagi boşsa siparisId'den kaynak çıkar (örn: SP...-QR-1 → QR)
          const qrOrders: MasaSiparisi[] = [];
          const ecomOrders: MasaSiparisi[] = [];
          const kioskOrdrs: MasaSiparisi[] = [];
          
          todayOrders.forEach(order => {
            // Önce siparisKaynagi / SyncSource'a bak
            let kaynak = (order.siparisKaynagi || '').toLowerCase();
            
            // siparisKaynagi boşsa, siparisId formatından kaynak çıkar
            if (!kaynak && order.siparisId) {
              // ✅ Kiosk siparişleri D- veya P- prefix ile başlar (ör: D-S-20260212-00001, P-S-20260212-00001)
              if (order.siparisId.startsWith('D-') || order.siparisId.startsWith('P-')) {
                kaynak = 'kiosk';
              } else {
                // Eski format: SP...-QR-1, SP...-KIOSK-1, SP...-ECOM-1
                const siparisIdParts = order.siparisId.split('-');
                if (siparisIdParts.length >= 2) {
                  const sourceType = siparisIdParts[siparisIdParts.length - 2].toLowerCase();
                  if (sourceType === 'qr') kaynak = 'qrmenu';
                  else if (sourceType === 'kiosk') kaynak = 'kiosk';
                  else if (sourceType === 'ecom' || sourceType === 'ecommerce') kaynak = 'ecommerce';
                }
              }
            }
            
            console.log('📦 [MasaSiparisi] Sipariş kaynağı:', order.siparisId, '→', kaynak || 'UNKNOWN');
            
            if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce') || kaynak.includes('ecom')) {
              ecomOrders.push(order);
            } else if (kaynak.includes('kiosk')) {
              kioskOrdrs.push(order);
            } else if (kaynak.includes('qr') || kaynak.includes('qrmenu')) {
              qrOrders.push(order);
            }
          });
          
          // ✅ ŞUBE FİLTRESİ: Eğer ekrana şube tanımlıysa, sadece o şubenin siparişlerini göster
          const filteredQrOrders = qrOrders.filter(order => isQrOrderForThisBranch(order));
          const filteredEcomOrders = ecomOrders.filter(order => isQrOrderForThisBranch(order));
          const filteredKioskOrders = kioskOrdrs.filter(order => isQrOrderForThisBranch(order));
          
          console.log(`📦 [MasaSiparisi] Yüklendi (${Math.round(performance.now() - t0)}ms):`, {
            qr: `${filteredQrOrders.length}/${qrOrders.length}`,
            ecommerce: `${filteredEcomOrders.length}/${ecomOrders.length}`,
            kiosk: `${filteredKioskOrders.length}/${kioskOrdrs.length}`,
            branchFilter: branchId || 'YOK (tüm şubeler)'
          });
          
          setQrMenuOrders(filteredQrOrders);
          setEcommerceOrders(filteredEcomOrders);
          setKioskOrders(filteredKioskOrders);

          // SQL emniyet ağı (QR Menü + Kiosk): ilk yükleme sessizce tohumlanır,
          // sonraki turlarda push'u kaçırılmış yeni sipariş için onNewOrder tetiklenir.
          // Şube süzgeci yukarıda uygulandı; liste zaten bu ekrana ait siparişler.
          const masaCandidates: Array<{ order: MasaSiparisi; source: MasaOrderSource }> = [
            ...filteredQrOrders.map(order => ({ order, source: 'qrmenu' as MasaOrderSource })),
            ...filteredKioskOrders.map(order => ({ order, source: 'kiosk' as MasaOrderSource })),
          ];
          const missedMasa = applyNewOrderSafetyNet(
            masaCandidates,
            masaSafetySeenRef.current,
            masaSafetySeededRef,
            (item) => safetyNetOrderId(item.order.id, item.order.siparisId),
            (item) => isMasaNewOrderNotifiableStatus(item.order.siparisDurumu),
          );
          for (const { order, source } of missedMasa) {
            console.log('[MasaSiparisi] Safety-net: kaçırılmış yeni sipariş bildirimi telafi ediliyor:', order.siparisId);
            onNewOrderRef.current?.({
              ...order,
              siparisKaynagi: order.siparisKaynagi || (source === 'kiosk' ? 'Kiosk' : 'qrmenu'),
              notificationKey: masaNewOrderNotificationKey(source, order),
            });
          }
        }
    } catch (error) {
      console.error('❌ [MasaSiparisi] Yükleme hatası:', error);
    }
  }, [companyId, branchId, isQrOrderForThisBranch]);
  
  /**
   * Getir siparişlerini API'den yükle (bugünün siparişleri)
   */
  const fetchGetirOrders = useCallback(async () => {
    if (!companyId) return;
    const t0 = performance.now();
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.warn('⚠️ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil');
        return;
      }
      
      const { isExtendedDay, yesterday } = getBusinessDayInfo();
      let url: string;
      if (isExtendedDay && yesterday) {
        const params = new URLSearchParams();
        params.append('startDate', `${yesterday}T00:00:00`);
        params.append('endDate', new Date().toISOString());
        params.append('pageSize', '5000');
        if (branchId) params.append('branchId', branchId);
        url = `/api/getir-orders/list?${params.toString()}`;
      } else {
        url = branchId
          ? `/api/getir-orders/today?branchId=${branchId}`
          : `/api/getir-orders/today`;
      }
      console.log('🔄 [Getir] Siparişler yükleniyor:', url);
      const { data: result } = await getirApi.get(url);
      const data = result.data ?? result.Data;
      const success = result.success ?? result.Success;
      if (success && data) {
        setGetirOrders(data);
        console.log(`✅ [Getir] ${data.length} sipariş yüklendi (${Math.round(performance.now() - t0)}ms)`);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Token hatası (401) - token yenileme tetikleniyor...');
        onGetirTokenError?.();
      } else {
        console.error(`❌ [Getir] Sipariş yükleme hatası (${Math.round(performance.now() - t0)}ms):`, error);
      }
    }
  }, [companyId, branchId, onGetirTokenError]);
  fetchGetirOrdersRef.current = fetchGetirOrders;

  const fetchMigrosOrders = useCallback(async () => {
    if (!companyId) return;
    const t0 = performance.now();
    try {
      if (!process.env.REACT_APP_MIGROSAPI_URL) {
        console.warn('[Migros] REACT_APP_MIGROSAPI_URL tanimli degil');
        return;
      }
      const { isExtendedDay, yesterday } = getBusinessDayInfo();
      let url: string;
      if (isExtendedDay && yesterday) {
        const params = new URLSearchParams();
        params.append('startDate', `${yesterday}T00:00:00`);
        params.append('endDate', new Date().toISOString());
        params.append('pageSize', '5000');
        if (branchId) params.append('branchId', branchId);
        url = `/api/migros-orders/list?${params.toString()}`;
      } else {
        url = branchId
          ? `/api/migros-orders/today?branchId=${branchId}`
          : `/api/migros-orders/today`;
      }
      console.log('[Migros] Siparisler yukleniyor:', url);
      const { data: result } = await migrosApi.get(url);
      const data = result.data ?? result.Data;
      const success = result.success ?? result.Success;
      if (success && data) {
        setMigrosOrders(data);
        console.log(`[Migros] ${data.length} siparis yuklendi (${Math.round(performance.now() - t0)}ms)`);

        const list = (Array.isArray(data) ? data : []) as MigrosOrder[];
        const missed = applyNewOrderSafetyNet(
          list,
          migrosSafetySeenRef.current,
          migrosSafetySeededRef,
          (order) => safetyNetOrderId(order.id, order.migrosOrderId),
          (order) => isMigrosNewOrderNotifiableStatus(order.status),
        );
        const currentBranchId = branchIdRef.current;
        for (const order of missed) {
          if (currentBranchId && order.branchId && String(order.branchId) !== String(currentBranchId)) {
            continue;
          }
          console.log('[Migros] Safety-net: kaçırılmış yeni sipariş bildirimi telafi ediliyor:', order.shortCode || order.migrosOrderId);
          onNewOrderRef.current?.({
            ...mapMigrosOrderToMasaSiparisi(order),
            notificationKey: migrosNewOrderNotificationKey(order),
          });
        }
      }
    } catch (error) {
      console.error(`[Migros] Siparis yukleme hatasi (${Math.round(performance.now() - t0)}ms):`, error);
    }
  }, [companyId, branchId]);
  fetchMigrosOrdersRef.current = fetchMigrosOrders;

  /**
   * Durum guncellemesi suren Migros siparisleri (orderId). Sag panel butonu ile
   * yeni siparis modalindaki onay ayni kilidi paylasir; ayni siparise ustuste
   * istek gitmesini engeller.
   */
  const migrosStatusInFlightRef = useRef<Set<number>>(new Set());

  const updateMigrosOrderStatus = useCallback(async (orderId: number, newStatus: string, cancelReasonId?: number): Promise<MigrosStatusUpdateResult> => {
    if (!companyId) return { success: false, message: 'CompanyId yok' };

    // Ayni siparise es zamanli ikinci istek gonderilmesin. Sag paneldeki buton ve
    // yeni siparis modalindaki "Onayla ve Yazdir" ayni fonksiyonu cagiriyor; ortak
    // kilit burada olunca iki yol da korunur (aksi halde tek siparis icin ustuste
    // onay istekleri gidip Migros tarafindan reddediliyordu).
    if (migrosStatusInFlightRef.current.has(orderId)) {
      console.warn('[Migros] Ayni siparis icin durum guncelleme zaten suruyor, istek atlandi:', orderId);
      return { success: false, message: 'Bu siparis icin islem zaten suruyor.', inFlight: true };
    }
    migrosStatusInFlightRef.current.add(orderId);

    try {
      if (!process.env.REACT_APP_MIGROSAPI_URL) return { success: false, message: 'Migros API URL tanimli degil' };
      const { data: result } = await migrosApi.post('/api/migros-orders/update-status', {
        OrderId: orderId,
        NewStatus: newStatus,
        CancelReasonId: cancelReasonId,
      });
      if (result.success) {
        setMigrosOrders(prev => prev.map(o =>
          o.id === orderId ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o
        ));
        return { success: true, message: result.message };
      }
      // Backend self-heal yanitlarini yakala (PascalCase veya camelCase olabilir)
      const alreadyCancelled = result.alreadyCancelled ?? result.AlreadyCancelled;
      const selfHealed = result.selfHealed ?? result.SelfHealed;
      const statusMismatch = result.statusMismatch ?? result.StatusMismatch;
      // Siparis Migros'ta zaten istenen/ayni durumda: hata degil, bilgi.
      const alreadyInStatus = result.alreadyInStatus ?? result.AlreadyInStatus;
      const migrosStatus = result.migrosStatus ?? result.MigrosStatus;

      // Yerel state'i Migros'taki gercek statusla esitle (kullanici listeyi anında dogru gorsun).
      if ((selfHealed || alreadyInStatus) && migrosStatus) {
        setMigrosOrders(prev => prev.map(o =>
          o.id === orderId ? { ...o, status: migrosStatus, updatedAt: new Date().toISOString() } : o
        ));
      }

      return {
        success: false,
        message: result.message || 'Durum guncellenemedi',
        alreadyCancelled: !!alreadyCancelled,
        selfHealed: !!selfHealed,
        statusMismatch: !!statusMismatch,
        alreadyInStatus: !!alreadyInStatus,
        migrosStatus,
      };
    } catch (error: any) {
      console.error('[Migros] Durum guncelleme hatasi:', error);
      return { success: false, message: error.response?.data?.message || 'Migros API baglanti hatasi' };
    } finally {
      migrosStatusInFlightRef.current.delete(orderId);
    }
  }, [companyId]);

  const pollMigrosOrders = useCallback(async () => {
    if (!companyId) return;
    try {
      if (!process.env.REACT_APP_MIGROSAPI_URL) return;
      await migrosApi.post('/api/migros-orders/poll-pending', { BranchId: branchId });
      await fetchMigrosOrders();
    } catch (error) {
      console.error('[Migros] Polling hatasi:', error);
    }
  }, [companyId, branchId, fetchMigrosOrders]);

  const checkMigrosRestaurantStatus = useCallback(async (integrationId?: number) => {
    try {
      if (!process.env.REACT_APP_MIGROSAPI_URL || !companyId) return;
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (integrationId) params.append('integrationId', String(integrationId));
      const url = `/api/migros-orders/store-status?${params}`;
      console.log('🏪 [Migros] Restoran durumu sorgulanıyor:', url);
      const { data: result } = await migrosApi.get(url);
      console.log('🏪 [Migros] Restoran durumu API yanıtı:', JSON.stringify(result));
      if (result.success && result.data) {
        const data = result.data;
        const status = (data.status || '').toUpperCase();
        const isOpen = status === 'OPEN' || status === 'NOT_IN_WORK_HOURS';
        const storeKey = integrationId || result.IntegrationId || result.integrationId || DEFAULT_STORE_KEY;
        setMigrosRestaurantOpenMap(prev => ({ ...prev, [storeKey]: isOpen }));
        if (status === 'NOT_IN_WORK_HOURS' && data.openingTime) {
          const openTime = new Date(data.openingTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
          console.log(`🏪 [Migros] Restoran durumu: AKTIF ✅ (Calisme saati disinda - acilis: ${openTime})`);
        } else {
          console.log(`🏪 [Migros] Restoran durumu: ${isOpen ? 'ACIK ✅' : 'KAPALI ❌'} (status=${status})`);
        }
      } else {
        console.warn('🏪 [Migros] Restoran durumu alinamadi:', result.message);
      }
    } catch (error: any) {
      console.error('❌ [Migros] Restoran durumu hatasi:', error);
    }
  }, [companyId, branchId]);

  const openMigrosRestaurant = useCallback(async (integrationId?: number): Promise<boolean> => {
    try {
      const migrosApiUrl = process.env.REACT_APP_MIGROSAPI_URL;
      console.log('🏪 [Migros] Restoran ACMA istegi gonderiliyor...', { migrosApiUrl, companyId, branchId, integrationId });
      if (!migrosApiUrl || !companyId) {
        console.error('🏪 [Migros] Restoran acma iptal: migrosApiUrl veya companyId yok', { migrosApiUrl, companyId });
        return false;
      }
      const { data: result } = await migrosApi.post('/api/migros-orders/store-status/open', {
        BranchId: branchId,
        IntegrationId: integrationId,
      });
      console.log('🏪 [Migros] Restoran acma response body:', result);
      if (result.success) {
        setMigrosRestaurantOpenMap(prev => ({ ...prev, [integrationId || result.IntegrationId || DEFAULT_STORE_KEY]: true }));
        console.log('✅ [Migros] Restoran ACILDI!');
        return true;
      }
      console.warn('⚠️ [Migros] Restoran acilamadi:', result.message || 'Bilinmeyen hata');
      return false;
    } catch (error) {
      console.error('❌ [Migros] Restoran acma hatasi:', error);
      return false;
    }
  }, [companyId, branchId]);

  const closeMigrosRestaurant = useCallback(async (integrationId?: number): Promise<boolean> => {
    try {
      const migrosApiUrl = process.env.REACT_APP_MIGROSAPI_URL;
      console.log('🏪 [Migros] Restoran KAPATMA istegi gonderiliyor...', { migrosApiUrl, companyId, branchId, integrationId });
      if (!migrosApiUrl || !companyId) {
        console.error('🏪 [Migros] Restoran kapatma iptal: migrosApiUrl veya companyId yok', { migrosApiUrl, companyId });
        return false;
      }
      const { data: result } = await migrosApi.post('/api/migros-orders/store-status/close', {
        BranchId: branchId,
        IntegrationId: integrationId,
      });
      console.log('🏪 [Migros] Restoran kapatma response body:', result);
      if (result.success) {
        setMigrosRestaurantOpenMap(prev => ({ ...prev, [integrationId || result.IntegrationId || DEFAULT_STORE_KEY]: false }));
        console.log('✅ [Migros] Restoran KAPATILDI!');
        return true;
      }
      console.warn('⚠️ [Migros] Restoran kapatılamadi:', result.message || 'Bilinmeyen hata');
      return false;
    } catch (error) {
      console.error('❌ [Migros] Restoran kapatma hatasi:', error);
      return false;
    }
  }, [companyId, branchId]);

  // ============================================================================
  // YEMEKSEPETI FONKSIYONLARI
  // ============================================================================

  const fetchYemeksepetiOrders = useCallback(async () => {
    if (!companyId) return;
    const t0 = performance.now();
    try {
      if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
        console.warn('[YS] REACT_APP_YEMEKSEPETIAPI_URL tanimli degil');
        return;
      }
      const { isExtendedDay, yesterday } = getBusinessDayInfo();
      let url: string;
      if (isExtendedDay && yesterday) {
        const params = new URLSearchParams();
        params.append('startDate', `${yesterday}T00:00:00`);
        params.append('endDate', new Date().toISOString());
        params.append('pageSize', '5000');
        if (branchId) params.append('branchId', branchId);
        url = `/api/ys-orders/list?${params.toString()}`;
      } else {
        url = branchId
          ? `/api/ys-orders/today?branchId=${branchId}`
          : `/api/ys-orders/today`;
      }
      console.log('[YS] Siparisler yukleniyor:', url);
      const { data: result } = await yemeksepetiApi.get(url);
      const success = result.Success ?? result.success;
      const data = result.Data ?? result.data;
      if (success && data) {
        setYemeksepetiOrders(data);
        console.log(`[YS] ${data.length} siparis yuklendi (${Math.round(performance.now() - t0)}ms)`);

        const list = (Array.isArray(data) ? data : []) as YemeksepetiOrder[];
        const missed = applyNewOrderSafetyNet(
          list,
          ysSafetySeenRef.current,
          ysSafetySeededRef,
          (order) => safetyNetOrderId(order.id, order.token || order.remoteOrderId),
          (order) => isYsNewOrderNotifiableStatus(order.status),
        );
        const currentBranchId = branchIdRef.current;
        for (const order of missed) {
          if (currentBranchId && order.branchId && String(order.branchId) !== String(currentBranchId)) {
            continue;
          }
          console.log('[YS] Safety-net: kaçırılmış yeni sipariş bildirimi telafi ediliyor:', order.shortCode || order.code || order.id);
          onNewOrderRef.current?.({
            ...mapYsOrderToMasaSiparisi(order),
            notificationKey: ysNewOrderNotificationKey(order),
          });
        }
      } else {
        console.warn('[YS] Siparis verisi bos veya basarisiz:', result);
      }
    } catch (error) {
      console.error(`[YS] Siparis yukleme hatasi (${Math.round(performance.now() - t0)}ms):`, error);
    }
  }, [companyId, branchId]);
  fetchYemeksepetiOrdersRef.current = fetchYemeksepetiOrders;

  const updateYemeksepetiOrderStatus = useCallback(async (orderId: number, action: string, reason?: string): Promise<{ success: boolean; message?: string }> => {
    if (!companyId) return { success: false, message: 'CompanyId yok' };
    try {
      if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL) return { success: false, message: 'YS API URL tanimli degil' };
      const endpoint = action === 'accept' ? 'accept' : action === 'reject' ? 'reject' : 'prepared';
      const body: any = { OrderId: orderId };
      if (action === 'reject' && reason) body.Reason = reason;
      if (action === 'accept') body.PreparationTimeMinutes = 30;

      console.log(`[YS] ${action} istegi gonderiliyor:`, body);
      const { data: result } = await yemeksepetiApi.post(`/api/ys-orders/${endpoint}`, body);
      const success = result.Success ?? result.success;
      const message = result.Message ?? result.message;
      if (success) {
        console.log(`[YS] ${action} basarili:`, message);
        // İyimser güncelleme: durum butonu tam liste yenilemesini beklemeden değişsin.
        // NOT: Tüm çağıranlar (useOrderStatusHandlers, AutoAccept, reject modalı) başarıdan
        // sonra zaten fetchYemeksepetiOrders çağırıyor; buradaki ikinci (mükerrer) await
        // kaldırıldı — yavaş internette onayın "takılı" görünmesine neden oluyordu.
        const optimisticStatus =
          action === 'accept' ? 'ACCEPTED' :
          action === 'prepared' ? 'PREPARED' :
          action === 'reject' ? 'REJECTED' : null;
        if (optimisticStatus) {
          setYemeksepetiOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, status: optimisticStatus } : o
          ));
        }
        return { success: true, message };
      }
      // HTTP 200 + Success=false: API'ye ulastik ama Yemeksepeti islemi reddetti.
      // Sessiz kalirsa oto onay akisinda hatanin izi kalmiyor.
      console.error(`[YS] ${action} REDDEDILDI (HTTP 200, Success=false):`, { orderId, message, result });
      return { success: false, message: message || `${action} basarisiz` };
    } catch (error: any) {
      console.error(`[YS] ${action} hatasi:`, error);
      return { success: false, message: error.response?.data?.message || String(error) };
    }
  }, [companyId]);

  const checkYemeksepetiVendorStatus = useCallback(async (integrationId?: number) => {
    try {
      if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL || !companyId) return;
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (integrationId) params.append('integrationId', String(integrationId));
      const url = `/api/ys-orders/vendor-status?${params}`;
      console.log('[YS] Vendor durumu sorgulanıyor:', url);
      const { data: result } = await yemeksepetiApi.get(url);
      const success = result.Success ?? result.success;
      const data = result.Data ?? result.data;
      const items = data?.Items ?? data?.items;
      if (success && items && Array.isArray(items)) {
        const isOpen = items.some((item: any) => (item.AvailabilityState ?? item.availabilityState) === 'OPEN');
        const storeKey = integrationId || result.IntegrationId || result.integrationId || DEFAULT_STORE_KEY;
        setYemeksepetiVendorOpenMap(prev => ({ ...prev, [storeKey]: isOpen }));
        console.log(`[YS] Vendor durumu: ${isOpen ? 'ACIK' : 'KAPALI'}`);
      }
    } catch (error) {
      console.error('[YS] Vendor durumu hatasi:', error);
    }
  }, [companyId, branchId]);

  const openYemeksepetiVendor = useCallback(async (integrationId?: number): Promise<boolean> => {
    try {
      if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL || !companyId) return false;
      console.log('[YS] Vendor ACMA istegi gonderiliyor...', { integrationId });
      const { data: result } = await yemeksepetiApi.post('/api/ys-orders/vendor-status/open', {
        BranchId: branchId,
        IntegrationId: integrationId,
      });
      const success = result.Success ?? result.success;
      const message = result.Message ?? result.message;
      if (success) {
        const storeKey = integrationId || result.IntegrationId || result.integrationId || DEFAULT_STORE_KEY;
        setYemeksepetiVendorOpenMap(prev => ({ ...prev, [storeKey]: true }));
        console.log('[YS] Vendor ACILDI!');
        return true;
      }
      console.warn('[YS] Vendor acilamadi:', message);
      return false;
    } catch (error) {
      console.error('[YS] Vendor acma hatasi:', error);
      return false;
    }
  }, [companyId, branchId]);

  const closeYemeksepetiVendor = useCallback(async (closedReason?: string, integrationId?: number): Promise<boolean> => {
    try {
      if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL || !companyId) return false;
      const body = {
        BranchId: branchId,
        IntegrationId: integrationId,
        ClosedReason: closedReason || 'OTHER',
        AvailabilityState: 'CLOSED_TODAY',
      };
      console.log('[YS] Vendor KAPATMA istegi gonderiliyor...', { closedReason, integrationId });
      const { data: result } = await yemeksepetiApi.post('/api/ys-orders/vendor-status/close', body);
      const success = result.Success ?? result.success;
      const message = result.Message ?? result.message;
      if (success) {
        const storeKey = integrationId || result.IntegrationId || result.integrationId || DEFAULT_STORE_KEY;
        setYemeksepetiVendorOpenMap(prev => ({ ...prev, [storeKey]: false }));
        console.log('[YS] Vendor KAPATILDI!');
        return true;
      }
      console.warn('[YS] Vendor kapatilamadi:', message);
      return false;
    } catch (error) {
      console.error('[YS] Vendor kapatma hatasi:', error);
      return false;
    }
  }, [companyId, branchId]);

  /**
   * Getir siparis durumunu guncelle
   */
  const updateGetirOrderStatus = useCallback(async (orderId: number, newStatus: string): Promise<{ success: boolean; message?: string }> => {
    if (!companyId) {
      console.error('❌ [Getir] CompanyId yok');
      return { success: false, message: 'CompanyId yok' };
    }
    
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.error('❌ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil');
        return { success: false, message: 'Getir API URL tanımlı değil' };
      }
      
      console.log('🔄 [Getir] Durum güncelleniyor:', orderId, '->', newStatus);
      const { data: result } = await getirApi.post('/api/getir-orders/update-status', {
        orderId: orderId,
        newStatus: newStatus,
      });
      
      if (result.success) {
        console.log('✅ [Getir] Durum güncellendi:', orderId, '->', newStatus);
        // İyimser güncelleme: sayısal `status` da güncellenir ki durum butonu
        // (getStatusButtonInfo) tüm listenin yeniden çekilmesini beklemeden değişsin.
        // Kodlar backend UpdateOrderStatusInDbAsync ile birebir aynı eşleme.
        const statusCodeMap: Record<string, number> = { verify: 500, prepare: 550, handover: 600, deliver: 900 };
        const optimisticStatus = statusCodeMap[newStatus.toLowerCase()];
        setGetirOrders(prev => prev.map(order => 
          order.id === orderId
            ? {
                ...order,
                ...(optimisticStatus !== undefined ? { status: optimisticStatus } : {}),
                statusText: result.message || newStatus,
                updatedAt: new Date().toISOString(),
              }
            : order
        ));
        return { success: true, message: result.message };
      }
      return { success: false, message: result.message || 'Getir sipariş durumu güncellenemedi' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Token hatası (401) - token yenileme tetikleniyor...');
        onGetirTokenError?.();
        return { success: false, message: 'Getir token süresi dolmuş, yenileniyor...' };
      }
      console.error('❌ [Getir] Durum güncelleme hatası:', error);
      return { success: false, message: error.response?.data?.message || 'Getir API bağlantı hatası' };
    }
  }, [companyId, onGetirTokenError]);

  /**
   * Getir restoran açık/kapalı durumunu kontrol et
   * PlatformIntegrations'tan companyId/branchId ile credential lookup yapar
   */
  const checkGetirRestaurantStatus = useCallback(async (integrationId?: number) => {
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.warn('⚠️ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil - restoran durumu kontrol edilemiyor');
        return;
      }
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (integrationId) params.append('integrationId', String(integrationId));
      const url = params.toString() ? `/api/Restaurant/status?${params.toString()}` : '/api/Restaurant/status';
      console.log('🏪 [Getir] Restoran durumu sorgulanıyor:', url);
      const { data: result } = await getirApi.get(url);
      const isOpen = result.isOpen === true;
      const storeKey = integrationId || result.integrationId || DEFAULT_STORE_KEY;
      setGetirRestaurantOpenMap(prev => ({ ...prev, [storeKey]: isOpen }));
      console.log(`🏪 [Getir] Restoran durumu: ${isOpen ? 'AÇIK ✅' : 'KAPALI ❌'} (RestaurantId: ${result.restaurantId})`);
      // Backend "kapalı" cevabını HTTP 200 + error alaniyla döner (credential
      // bulunamadi, login basarisiz, POS aktif degil...). Bu alan okunmazsa
      // gerçek arıza sebebi kaybolur ve ekran sadece "KAPALI" gösterir.
      if (!isOpen && result.error) {
        console.warn(`🏪 [Getir] Kapalı görünmesinin sebebi (integrationId=${storeKey}):`, result.error);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Restoran durumu token hatası (401) - token yenileme tetikleniyor...');
        onGetirTokenError?.();
      } else {
        console.error('❌ [Getir] Restoran durumu sorgulanırken hata:', error);
      }
    }
  }, [companyId, branchId, onGetirTokenError]);

  /**
   * Getir restoranını aç
   * PlatformIntegrations'tan companyId/branchId ile credential lookup yapar
   */
  const openGetirRestaurant = useCallback(async (integrationId?: number): Promise<{ success: boolean; message?: string }> => {
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.error('❌ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil');
        return { success: false, message: 'Getir API URL tanımlı değil' };
      }
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (integrationId) params.append('integrationId', String(integrationId));
      const url = params.toString() ? `/api/Restaurant/open?${params.toString()}` : '/api/Restaurant/open';
      console.log('🔄 [Getir] Restoran açılıyor...', url);
      const { data: result } = await getirApi.post(url);
      if (result.success) {
        console.log('✅ [Getir] Restoran başarıyla açıldı');
        setGetirRestaurantOpenMap(prev => ({ ...prev, [integrationId || result.integrationId || DEFAULT_STORE_KEY]: true }));
        return { success: true };
      }
      console.error('❌ [Getir] Restoran açılamadı:', result);
      return { success: false, message: result?.message };
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Restoran açma token hatası (401) - token yenileme tetikleniyor...');
        onGetirTokenError?.();
        return { success: false, message: 'Getir oturumu doğrulanamadı (401)' };
      }
      console.error('❌ [Getir] Restoran açılırken hata:', error);
      return { success: false, message: error.response?.data?.message || error.message };
    }
  }, [companyId, branchId, onGetirTokenError]);

  const closeGetirRestaurant = useCallback(async (integrationId?: number): Promise<boolean> => {
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.error('❌ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil');
        return false;
      }
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (integrationId) params.append('integrationId', String(integrationId));
      const url = params.toString() ? `/api/Restaurant/close?${params.toString()}` : '/api/Restaurant/close';
      console.log('🔄 [Getir] Restoran kapatılıyor...', url);
      const { data: result } = await getirApi.post(url);
      if (result.success) {
        console.log('✅ [Getir] Restoran başarıyla kapatıldı');
        setGetirRestaurantOpenMap(prev => ({ ...prev, [integrationId || result.integrationId || DEFAULT_STORE_KEY]: false }));
        return true;
      }
      console.error('❌ [Getir] Restoran kapatılamadı:', result);
      return false;
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Restoran kapatma token hatası (401)');
        onGetirTokenError?.();
      } else {
        console.error('❌ [Getir] Restoran kapatılırken hata:', error);
      }
      return false;
    }
  }, [companyId, branchId, onGetirTokenError]);

  /**
   * Getir API'den aktif siparişlerin güncel durumlarını senkronize et
   * Dış kaynaktan (Getir panel, kurye app, vb.) değişen durumları yakalar
   * 1 dk polling ve yenile butonuyla çağrılır
   */
  const syncGetirOrderStatuses = useCallback(async (): Promise<{ updatedCount: number }> => {
    if (!companyId) return { updatedCount: 0 };
    
    try {
      if (!process.env.REACT_APP_GETIRAPI_URL) {
        console.warn('⚠️ [Getir] REACT_APP_GETIRAPI_URL tanımlı değil - sync atlanıyor');
        return { updatedCount: 0 };
      }
      
      console.log('🔄 [Getir] Sipariş durumları senkronize ediliyor...');
      const { data: result } = await getirApi.post('/api/getir-orders/sync-statuses', {
        branchId: branchId || null,
      });
      
      if (result.success && result.updatedCount > 0) {
        console.log(`✅ [Getir] ${result.updatedCount}/${result.totalChecked} sipariş durumu güncellendi:`, result.updatedOrders);
        setGetirOrders(prev => {
          let updated = [...prev];
          for (const change of result.updatedOrders) {
            updated = updated.map(order => 
              order.getirId === change.getirId
                ? { ...order, status: change.newStatus, statusText: change.statusText, updatedAt: new Date().toISOString() }
                : order
            );
          }
          return updated;
        });
        return { updatedCount: result.updatedCount };
      }
      console.log(`✅ [Getir] Senkronizasyon tamamlandı - değişiklik yok (${result.totalChecked} kontrol edildi)`);
      return { updatedCount: 0 };
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('⚠️ [Getir] Sync token hatası (401) - token yenileme tetikleniyor...');
        onGetirTokenError?.();
      } else {
        console.error('❌ [Getir] Senkronizasyon hatası:', error);
      }
      return { updatedCount: 0 };
    }
  }, [companyId, branchId, onGetirTokenError]);

  /**
   * MasaSiparisi durumunu güncelle (sadece onay - iptal/sil YOK)
   */
  const updateMasaSiparisiStatus = useCallback(async (orderId: number, newStatus: string): Promise<boolean> => {
    if (!companyId) {
      console.error('❌ [MasaSiparisi] CompanyId yok');
      return false;
    }
    
    try {
      console.log('🔄 [MasaSiparisi] Durum güncelleniyor:', orderId, '->', newStatus);
      const { data: result } = await mainApi.post(`/api/MasaSiparisi/order/${orderId}/status`, {
        siparisDurumu: newStatus,
      });
      if (result.success) {
        console.log('✅ [MasaSiparisi] Durum güncellendi:', orderId, '->', newStatus);
        const updateOrder = (prev: MasaSiparisi[]) => prev.map(order => 
          order.id === orderId
            ? { ...order, siparisDurumu: newStatus, siparisGuncellenmeTarihi: new Date().toISOString() }
            : order
        );
        setQrMenuOrders(updateOrder);
        setEcommerceOrders(updateOrder);
        setKioskOrders(updateOrder);
        return true;
      }
      console.error('❌ [MasaSiparisi] Durum güncelleme hatası');
      return false;
    } catch (error) {
      console.error('❌ [MasaSiparisi] Durum güncelleme hatası:', error);
      return false;
    }
  }, [companyId]);
  
  // ============================================================================
  // SIGNALR BAĞLANTISI
  // ============================================================================
  
  useEffect(() => {
    if (!companyId) {
      console.log('🔌 [SignalR] CompanyId yok, bağlantı kurulamıyor');
      return;
    }
    
    let stopConnection: (() => void) | null = null;

    const setupConnection = async () => {
      try {
        console.log('🔌 [SignalR] Bağlantı kuruluyor...');
        
        const apiUrl = process.env.REACT_APP_NETAPI_URL;
        if (!apiUrl) {
          console.error('🔌 [SignalR] REACT_APP_NETAPI_URL tanımlı değil');
          setStatus('Error');
          return;
        }
        
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(`${apiUrl}/ordershub`, {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect(infiniteRetryPolicy)
          .configureLogging(signalR.LogLevel.None)
          .build();
        
        // ✅ YENİ SİPARİŞ
        connection.on('NewOrder', (orderData) => {
          // ✅ GETİR SİPARİŞİ KONTROLÜ - Getir siparişleri "type: getir" wrapper'ı ile gelir
          if (orderData?.type === 'getir' && orderData?.orderData) {
            const getirData = orderData.orderData;
            console.log('🔔 [SignalR] YENİ GETİR SİPARİŞİ:', getirData.getirId || getirData.confirmationId);

            // Şube filtresi - en güncel branchId'yi ref'ten al (stale closure'a karşı koruma).
            // Push'taki branchId hem wrapper'da hem de iç data'da olabilir; ikisine de bak.
            const currentBranchId = branchIdRef.current;
            const pushBranchId = orderData.branchId ?? getirData?.branchId ?? getirData?.BranchId;
            if (currentBranchId && pushBranchId && String(pushBranchId) !== String(currentBranchId)) {
              console.log('⏭️ [SignalR] Getir sipariş başka şubeye ait, atlanıyor', {
                currentBranch: currentBranchId,
                orderBranch: pushBranchId,
              });
              return;
            }
            
            // Duplicate kontrolü
            const getirKey = `getir_${getirData.getirId || getirData.id}`;
            if (processedOrdersRef.current.has(getirKey)) {
              console.log('⏭️ [SignalR] DUPLICATE Getir sipariş:', getirKey);
              return;
            }
            processedOrdersRef.current.add(getirKey);
            setTimeout(() => processedOrdersRef.current.delete(getirKey), 30000);
            
            // Getir siparişini listeye ekle
            const getirOrder: GetirOrder = {
              id: getirData.id,
              getirId: getirData.getirId,
              confirmationId: getirData.confirmationId,
              status: getirData.status,
              statusText: getirData.statusText,
              companyId: getirData.companyId,
              branchId: getirData.branchId,
              clientName: getirData.musteriAdi,
              clientPhone: getirData.musteriTelefon,
              deliveryAddress: getirData.musteriAdres,
              clientNote: getirData.clientNote,
              totalPrice: getirData.toplamTutar || getirData.totalPrice,
              totalDiscountedPrice: (getirData.totalDiscountedPrice != null && getirData.totalDiscountedPrice > 0) ? getirData.totalDiscountedPrice : undefined,
              isScheduled: getirData.isScheduled || false,
              scheduledDate: getirData.scheduledDate || undefined,
              deliveryType: getirData.deliveryType,
              paymentMethodText: getirData.paymentMethodText,
              checkoutDate: getirData.siparisOlusturmaTarihi,
              createdAt: getirData.siparisOlusturmaTarihi || new Date().toISOString(),
              // Çoklu mağaza damgası — sipariş hangi marka altından geldi
              restaurantId: getirData.restaurantId ?? getirData.RestaurantId,
              platformIntegrationId: getirData.platformIntegrationId ?? getirData.PlatformIntegrationId,
              storeName: getirData.storeName ?? getirData.StoreName,
              items: getirData.siparisDetaylari?.map((item: any) => ({
                id: 0,
                name: item.urunAdi,
                quantity: item.adet,
                price: item.birimFiyat,
                totalPrice: item.toplamFiyat,
                note: item.notlar,
              })) || [],
            };
            
            setGetirOrders(prev => {
              const exists = prev.some(o => o.getirId === getirOrder.getirId);
              if (exists) return prev;
              return [getirOrder, ...prev];
            });
            
            // Bildirim modal'ı göster (MasaSiparisi formatına çevirerek)
            const masaSiparisiForModal: MasaSiparisi = {
              id: getirOrder.id,
              siparisId: getirOrder.confirmationId || getirOrder.getirId,
              siparisDurumu: 'Bekliyor',
              siparisOlusturmaTarihi: getirOrder.createdAt,
              toplamTutar: getirOrder.totalPrice,
              siparisDetaylari: getirOrder.items?.map(item => ({
                urunAdi: item.name,
                adet: item.quantity,
                birimFiyat: item.price,
                toplamFiyat: item.totalPrice,
                notlar: item.note,
              })) || [],
              siparisNotu: getirOrder.clientNote,
              siparisKaynagi: 'Getir',
              musteriAdi: getirOrder.clientName,
              musteriTelefon: getirOrder.clientPhone,
              musteriAdres: getirOrder.deliveryAddress,
              isPreOrder: getirOrder.isScheduled || false,
              expectedDeliveryTime: getirOrder.scheduledDate || undefined,
            };
            
            onNewOrder?.(masaSiparisiForModal);
            return;
          }
          
          if (orderData?.type === 'migros') {
            return;
          }
          if (orderData?.type === 'yemeksepeti') {
            return;
          }
          
          console.log('🔔 [SignalR] Yeni sipariş geldi:', orderData.siparisId, '- Kaynak:', orderData.siparisKaynagi, '- QRMenuId:', orderData.qrMenuId);
          
          // ✅ Hızlı Satış siparişlerini atla (HS siparişleri paket sipariş ekranına düşmemeli)
          if (orderData.siparisId?.includes('-HS-')) {
            console.log('⏭️ [SignalR] HIZLI SATIŞ - Paket ekranına eklenmeyecek:', orderData.siparisId);
            return;
          }
          
          // ✅ Restoran siparişlerini atla (RS siparişleri restoran ekranına ait, paket ekranına düşmemeli)
          if (orderData.siparisId?.startsWith('RS-')) {
            console.log('⏭️ [SignalR] RESTORAN SİPARİŞİ - Paket ekranına eklenmeyecek:', orderData.siparisId);
            return;
          }
          
          // Duplicate kontrolü
          const orderKey = `${orderData.id}_${orderData.siparisId}`;
          if (processedOrdersRef.current.has(orderKey)) {
            console.log('⏭️ [SignalR] DUPLICATE - Sipariş zaten işlendi:', orderData.siparisId);
            return;
          }
          processedOrdersRef.current.add(orderKey);
          setTimeout(() => processedOrdersRef.current.delete(orderKey), 30000);
          
          // Sipariş detaylarını parse et
          let siparisDetaylari: MasaSiparisDetay[] = [];
          try {
            if (typeof orderData.siparisDetaylari === 'string') {
              const parsed = JSON.parse(orderData.siparisDetaylari || '[]');
              siparisDetaylari = parsed.siparisDetaylari || parsed || [];
            } else {
              siparisDetaylari = orderData.siparisDetaylari?.siparisDetaylari || orderData.siparisDetaylari || [];
            }
          } catch (e) {
            console.error('Sipariş detayları parse hatası:', e);
          }
          
          const newOrder: MasaSiparisi = {
            id: orderData.id,
            siparisId: orderData.siparisId,
            masaNumarasi: orderData.masaNumarasi || orderData.masaId,
            masaAdi: orderData.masaAdi,
            kullaniciAdi: orderData.kullaniciAdi,
            siparisDurumu: orderData.siparisDurumu,
            siparisOlusturmaTarihi: orderData.siparisOlusturmaTarihi,
            toplamTutar: orderData.toplamTutar,
            qrMenuId: orderData.qrMenuId,
            branchId: orderData.branchId,
            siparisDetaylari: siparisDetaylari,
            siparisNotu: orderData.siparisNotu,
            siparisKaynagi: orderData.siparisKaynagi || orderData.syncSource,
            musteriAdi: orderData.musteriAdi || orderData.kullaniciAdi,
            musteriTelefon: orderData.musteriTelefon,
            musteriAdres: orderData.musteriAdres,
          };
          
          // Kaynağa göre ilgili listeye ekle
          const source = getOrderSource(newOrder);
          
          if (source === null) {
            console.log('⏭️ [SignalR] RESTORAN/HIZLI SATIŞ - Paket ekranına eklenmeyecek:', newOrder.siparisId, newOrder.siparisKaynagi);
            return;
          }
          
          // Şube filtresi kontrolü
          console.log('🔍 [SignalR] Şube filtresi kontrolü:', {
            siparisId: newOrder.siparisId,
            qrMenuId: newOrder.qrMenuId,
            branchId: branchId,
            allowedQrMenuIds: Array.from(allowedQrMenuIdsRef.current),
            isLoaded: allowedQrMenuIdsLoaded
          });
          
          if (!isQrOrderForThisBranch(newOrder)) {
            console.log('⏭️ [SignalR] ŞUBE FİLTRESİ - Bu şubeye ait değil, atlanıyor:', newOrder.siparisId);
            return;
          }
          console.log('✅ [SignalR] ŞUBE FİLTRESİ GEÇTİ - Sipariş gösterilecek:', newOrder.siparisId);

          // Outbox worker aynı siparişi yeniden yayınlarsa (hızlı yol 30 sn içinde
          // Succeeded işaretlenemediyse) ikinci kez ses çalmasın: kuyruk anahtarı
          // veritabanı kimliğinden üretilir, emniyet ağı da aynı anahtarı kullanır.
          const masaSource: MasaOrderSource =
            source === 'kiosk' ? 'kiosk' : source === 'ecommerce' ? 'ecommerce' : 'qrmenu';
          newOrder.notificationKey = masaNewOrderNotificationKey(masaSource, newOrder);
          markSafetyNetSeen(masaSafetySeenRef.current, safetyNetOrderId(newOrder.id, newOrder.siparisId));

          // Siparişi ilgili listeye ekle
          if (source === 'qr') {
            setQrMenuOrders(prev => {
              const exists = prev.some(o => o.id === newOrder.id);
              if (exists) return prev.map(o => o.id === newOrder.id ? newOrder : o);
              return [newOrder, ...prev];
            });
          } else if (source === 'ecommerce') {
            setEcommerceOrders(prev => {
              const exists = prev.some(o => o.id === newOrder.id);
              if (exists) return prev.map(o => o.id === newOrder.id ? newOrder : o);
              return [newOrder, ...prev];
            });
          } else if (source === 'kiosk') {
            setKioskOrders(prev => {
              const exists = prev.some(o => o.id === newOrder.id);
              if (exists) return prev.map(o => o.id === newOrder.id ? newOrder : o);
              return [newOrder, ...prev];
            });
          }
          
          // Callback'i çağır
          onNewOrder?.(newOrder);
        });
        
        // ✅ SİPARİŞ DURUMU GÜNCELLENDİ
        connection.on('OrderStatusUpdated', (orderData) => {
          console.log('🔄 [SignalR] Sipariş durumu güncellendi:', orderData.siparisId, '- Yeni Durum:', orderData.siparisDurumu);
          
          const updateOrder = (prev: MasaSiparisi[]) => {
            return prev.map(order => 
              (order.id === orderData.id || order.siparisId === orderData.siparisId)
                ? { ...order, siparisDurumu: orderData.siparisDurumu, siparisGuncellenmeTarihi: orderData.siparisGuncellenmeTarihi }
                : order
            );
          };
          
          setQrMenuOrders(updateOrder);
          setEcommerceOrders(updateOrder);
          setKioskOrders(updateOrder);
          
          // Callback'i çağır
          const updatedOrder: MasaSiparisi = {
            id: orderData.id,
            siparisId: orderData.siparisId,
            siparisDurumu: orderData.siparisDurumu,
            siparisGuncellenmeTarihi: orderData.siparisGuncellenmeTarihi,
            siparisOlusturmaTarihi: orderData.siparisOlusturmaTarihi,
            toplamTutar: orderData.toplamTutar,
            siparisDetaylari: [],
          };
          onOrderUpdated?.(updatedOrder);
        });
        
        // ✅ SİPARİŞ SİLİNDİ
        connection.on('OrderDeleted', (orderData) => {
          console.log('🗑️ [SignalR] Sipariş silindi:', orderData.id);
          
          const removeOrder = (prev: MasaSiparisi[]) => prev.filter(o => o.id !== orderData.id);
          
          setQrMenuOrders(removeOrder);
          setEcommerceOrders(removeOrder);
          setKioskOrders(removeOrder);
          
          // Callback'i çağır
          onOrderDeleted?.(orderData.id);
        });

        // ✅ GETİR SİPARİŞ İPTAL EDİLDİ (Getir/Admin tarafından)
        connection.on('GetirOrderCancelled', (cancelData) => {
          console.log('🚫 [SignalR] Getir sipariş iptal edildi:', cancelData);
          
          const orderData = cancelData?.orderData || cancelData;

          // ŞUBE FİLTRESİ: SignalR broadcast Company_{companyId} grubuna gidiyor,
          // yani aynı şirketteki tüm şubeler bildirimi alıyor. Bu ekran bir şubeye
          // tanımlıysa ve iptal edilen sipariş BAŞKA bir şubeye aitse hiçbir şey yapma.
          // branchIdRef ile stale closure'a karşı koruma.
          const currentBranchIdGetir = branchIdRef.current;
          const orderBranchId = orderData?.branchId ?? orderData?.BranchId;
          if (currentBranchIdGetir && orderBranchId && String(orderBranchId) !== String(currentBranchIdGetir)) {
            console.log('⏭️ [SignalR] Getir iptal başka şubeye ait, atlanıyor', {
              currentBranch: currentBranchIdGetir,
              orderBranch: orderBranchId,
            });
            return;
          }

          const cancelInfo: GetirCancelInfo = {
            getirId: orderData.getirId,
            confirmationId: orderData.confirmationId,
            status: orderData.status,
            cancelReasonText: orderData.cancelReasonText || 'Neden belirtilmedi',
            clientName: orderData.clientName,
            cancelledAt: orderData.cancelledAt || new Date().toISOString(),
          };
          
          // Getir siparişlerinde iptal edilen siparişin durumunu güncelle
          setGetirOrders(prev => prev.map(order => 
            order.getirId === cancelInfo.getirId
              ? { ...order, status: cancelInfo.status || 1600, statusText: 'İptal Edildi', updatedAt: cancelInfo.cancelledAt }
              : order
          ));
          
          // Callback'i çağır (modal açmak için)
          onGetirOrderCancelled?.(cancelInfo);
        });
        
        // MIGROS SIPARIS - NewOrder event'inda type === 'migros' kontrolu
        connection.on('NewOrder', (orderData) => {
          if (orderData?.type === 'migros' && orderData?.orderData) {
            const migrosData = orderData.orderData;
            console.log('[SignalR] YENI MIGROS SIPARISI:', migrosData.migrosOrderId || migrosData.id);

            // Şube filtresi - branchIdRef ile stale closure'a karşı koruma
            const currentBranchId = branchIdRef.current;
            const pushBranchId = orderData.branchId ?? migrosData?.branchId ?? migrosData?.BranchId;
            if (currentBranchId && pushBranchId && String(pushBranchId) !== String(currentBranchId)) {
              console.log('⏭️ [SignalR] Migros sipariş başka şubeye ait, atlanıyor', {
                currentBranch: currentBranchId,
                orderBranch: pushBranchId,
              });
              return;
            }

            const migrosKey = (typeof orderData.notificationKey === 'string' && orderData.notificationKey)
              ? orderData.notificationKey
              : migrosNewOrderNotificationKey(migrosData);
            if (processedOrdersRef.current.has(migrosKey)) return;
            processedOrdersRef.current.add(migrosKey);
            setTimeout(() => processedOrdersRef.current.delete(migrosKey), 30000);
            markSafetyNetSeen(
              migrosSafetySeenRef.current,
              safetyNetOrderId(migrosData.id ?? migrosData.Id, migrosData.migrosOrderId ?? migrosData.MigrosOrderId),
            );

            // Migros siparislerini yeniden yukle - ref ile en güncel fetcher'ı çağır
            // (stale closure ile branchId'siz fetch yapılmasını engeller)
            fetchMigrosOrdersRef.current?.();

            onNewOrder?.({
              ...mapMigrosOrderToMasaSiparisi(migrosData),
              notificationKey: migrosKey,
            });
          }
        });

        // MIGROS SIPARIS IPTAL
        connection.on('MigrosOrderCancelled', (cancelData) => {
          console.log('[SignalR] Migros siparis iptal:', cancelData);
          const orderData = cancelData?.orderData || cancelData;

          // ŞUBE FİLTRESİ: SignalR broadcast Company_{companyId} grubuna gidiyor,
          // yani aynı şirketteki tüm şubeler bildirimi alıyor. Bu ekran bir şubeye
          // tanımlıysa ve iptal edilen sipariş BAŞKA bir şubeye aitse hiçbir şey yapma.
          // branchIdRef ile stale closure'a karşı koruma.
          const currentBranchIdMigros = branchIdRef.current;
          const orderBranchId = orderData?.branchId ?? orderData?.BranchId;
          if (currentBranchIdMigros && orderBranchId && String(orderBranchId) !== String(currentBranchIdMigros)) {
            console.log('⏭️ [SignalR] Migros iptal başka şubeye ait, atlanıyor', {
              currentBranch: currentBranchIdMigros,
              orderBranch: orderBranchId,
            });
            return;
          }

          const cancelInfo: MigrosCancelInfo = {
            migrosOrderId: orderData.migrosOrderId,
            dbId: orderData.dbId,
            status: orderData.status || 'Cancelled',
            cancelReasonId: orderData.cancelReasonId,
            cancelledAt: new Date().toISOString(),
          };
          setMigrosOrders(prev => prev.map(o =>
            o.migrosOrderId === cancelInfo.migrosOrderId
              ? { ...o, status: cancelInfo.status, updatedAt: cancelInfo.cancelledAt }
              : o
          ));
          onMigrosOrderCancelled?.(cancelInfo);
        });

        // MIGROS TESLIMAT DURUM DEGISIKLIGI
        connection.on('MigrosDeliveryStatusChanged', (deliveryData) => {
          console.log('[SignalR] Migros teslimat durumu:', deliveryData);
          const data = deliveryData?.orderData || deliveryData;
          const statusInfo: MigrosDeliveryStatusInfo = {
            migrosOrderId: data.migrosOrderId,
            dbId: data.dbId,
            deliveryStatus: data.deliveryStatus,
            courierName: data.courierName,
            courierPhone: data.courierPhone,
            estimatedDeliveryTime: data.estimatedDeliveryTime,
          };
          onMigrosDeliveryStatusChanged?.(statusInfo);
        });

        // YEMEKSEPETI YENI SIPARIS
        connection.on('NewOrder', (orderData) => {
          if (orderData?.type === 'yemeksepeti' && orderData?.orderData) {
            const ysData = orderData.orderData;
            const ysId = ysData.Id ?? ysData.id;
            const ysToken = ysData.Token ?? ysData.token;
            const ysItems: any[] = ysData.Items ?? ysData.items ?? [];

            console.log('[SignalR] YENI YEMEKSEPETI SIPARISI:', ysId || ysData.Code || ysData.code, 'Items:', ysItems.length);

            // Şube filtresi - branchIdRef ile stale closure'a karşı koruma
            // (Bu sorun gerçek production'da gözlendi: push handler ilk mount'taki null
            //  branchId closure'ını yakalayıp tüm şubelerin siparişlerini canlı state'e
            //  yazıyordu — branchId'siz fetchYemeksepetiOrders sonucu.)
            const currentBranchId = branchIdRef.current;
            const pushBranchId = orderData.branchId ?? ysData?.branchId ?? ysData?.BranchId;
            if (currentBranchId && pushBranchId && String(pushBranchId) !== String(currentBranchId)) {
              console.log('⏭️ [SignalR] YS push başka şubeye ait, atlanıyor', {
                currentBranch: currentBranchId,
                orderBranch: pushBranchId,
              });
              return;
            }

            const ysKey = (typeof orderData.notificationKey === 'string' && orderData.notificationKey)
              ? orderData.notificationKey
              : ysNewOrderNotificationKey(ysData);
            if (processedOrdersRef.current.has(ysKey)) return;
            processedOrdersRef.current.add(ysKey);
            setTimeout(() => processedOrdersRef.current.delete(ysKey), 30000);
            markSafetyNetSeen(ysSafetySeenRef.current, safetyNetOrderId(ysId, ysToken));

            // Ref ile en güncel fetcher'ı çağır - stale closure ile branchId'siz fetch
            // yapılmasını engeller (asıl kirlilik kaynağı buradaydı)
            fetchYemeksepetiOrdersRef.current?.();

            onNewOrder?.({
              ...mapYsOrderToMasaSiparisi(ysData),
              notificationKey: ysKey,
            });
          }
        });

        // YEMEKSEPETI SIPARIS IPTALI
        connection.on('YemeksepetiOrderCancelled', (cancelData) => {
          console.log('[SignalR] Yemeksepeti siparis iptal:', cancelData);
          const data = cancelData?.orderData || cancelData;

          // ŞUBE FİLTRESİ: SignalR broadcast Company_{companyId} grubuna gidiyor,
          // yani aynı şirketteki tüm şubeler bildirimi alıyor. Bu ekran bir şubeye
          // tanımlıysa ve iptal edilen sipariş BAŞKA bir şubeye aitse hiçbir şey yapma.
          // branchIdRef ile stale closure'a karşı koruma.
          const currentBranchIdYs = branchIdRef.current;
          const orderBranchId = data?.branchId ?? data?.BranchId;
          if (currentBranchIdYs && orderBranchId && String(orderBranchId) !== String(currentBranchIdYs)) {
            console.log('⏭️ [SignalR] YS iptal başka şubeye ait, atlanıyor', {
              currentBranch: currentBranchIdYs,
              orderBranch: orderBranchId,
            });
            return;
          }

          const cancelInfo: YemeksepetiCancelInfo = {
            orderId: data.Id ?? data.id ?? data.orderId,
            status: data.Status ?? data.status ?? 'CANCELLED',
            customerName: data.CustomerFullName ?? data.customerFullName,
            cancelledAt: new Date().toISOString(),
            // Modal alan adlarını (`shortCode`, `customerPhone`, `deliveryAddress`,
            // `totalPrice`, `rawJsonData` vb.) `rawOrder` üzerinden çözer.
            rawOrder: data,
          };
          setYemeksepetiOrders(prev => prev.map(o =>
            o.id === cancelInfo.orderId ? { ...o, status: 'CANCELLED' } : o
          ));
          onYemeksepetiOrderCancelled?.(cancelInfo);
        });

        // ✅ TRENDYOL — server-side polling (local-service v1.4.3+) push.
        // Eski browser polling modunda bu event hiç gelmez (backend gönderdiğinde de
        // handler no-op olur çünkü PackageOrderScreen onTrendyolOrderUpdated geçmemiş
        // olabilir). Yeni mod'da PackageOrderScreen useTrendyolYemek.setTrendyolOrders
        // ile upsert yapar.
        connection.on('TrendyolOrderUpdated', (payload: unknown) => {
          try {
            const data = (payload || {}) as Record<string, unknown>;
            const orderRaw = (data.order ?? data.Order) as Record<string, unknown> | undefined;
            if (!orderRaw) {
              console.warn('[SignalR] TrendyolOrderUpdated payload.order yok:', payload);
              return;
            }

            // ŞUBE FİLTRESİ: SignalR broadcast Company_{companyId} grubuna gidiyor.
            // Bu ekran bir şubeye tanımlıysa ve sipariş başka şubeye aitse atla.
            // branchIdRef ile stale closure'a karşı koruma sağlanır.
            const currentBranchId = branchIdRef.current;
            const orderBranchId = (orderRaw.BranchId ?? orderRaw.branchId) as string | undefined;
            if (currentBranchId && orderBranchId && String(orderBranchId) !== String(currentBranchId)) {
              console.log('⏭️ [SignalR] Trendyol push başka şubeye ait, atlanıyor', {
                currentBranch: currentBranchId,
                orderBranch: orderBranchId,
              });
              return;
            }

            const mapped = mapSqlResponseToTrendyolOrder(orderRaw);
            if (!mapped) {
              console.warn('[SignalR] TrendyolOrderUpdated payload mapping başarısız:', orderRaw);
              return;
            }

            const isNew = !!(data.isNew ?? data.IsNew);
            const trKey = (typeof data.notificationKey === 'string' && data.notificationKey)
              ? data.notificationKey
              : platformNewOrderNotificationKey('trendyol', mapped.id);
            let duplicateNewOrder = false;
            if (isNew) {
              if (processedOrdersRef.current.has(trKey)) {
                duplicateNewOrder = true;
              } else {
                processedOrdersRef.current.add(trKey);
                setTimeout(() => processedOrdersRef.current.delete(trKey), 30000);
              }
            }
            const info: TrendyolOrderUpdatedInfo = {
              order: mapped,
              isNew,
              statusChanged: !!(data.statusChanged ?? data.StatusChanged),
              notificationKey: trKey,
              duplicateNewOrder,
            };
            console.log('🔔 [SignalR] TrendyolOrderUpdated:', {
              orderCode: mapped.orderCode,
              status: mapped.packageStatus,
              isNew: info.isNew,
              statusChanged: info.statusChanged,
            });
            onTrendyolOrderUpdatedRef.current?.(info);
          } catch (err) {
            console.error('[SignalR] TrendyolOrderUpdated handler hatası:', err);
          }
        });

        // ✅ Gruba katılım, bağlantının kendisi kadar kritik: backend şubeli
        // siparişi SADECE Branch_{branchId} grubuna yayınlıyor, bu gruba
        // katılamayan istemci hiçbir sipariş bildirimi almaz. Bu yüzden geçici
        // bir ağ hatasında sessizce vazgeçilmiyor, yeniden deneniyor.
        const joinGroups = async (): Promise<void> => {
          const attempts = 3;
          let lastError: unknown;

          for (let attempt = 1; attempt <= attempts; attempt++) {
            try {
              await connection.invoke('JoinCompanyGroup', companyId);
              console.log('✅ [SignalR] Şirket grubuna katıldı:', companyId);

              // ŞUBE İZOLASYONU: şubeli ekran kendi şube grubuna katılmak
              // ZORUNDA. Company grubuna yayın yapmak, bir şubenin siparişini
              // aynı şirketin diğer tüm şube ekranlarına düşürürdü.
              if (branchId) {
                await connection.invoke('JoinBranchGroup', branchId);
                console.log('✅ [SignalR] Şube grubuna katıldı:', branchId);
              }
              return;
            } catch (err) {
              lastError = err;
              console.warn(`⚠️ [SignalR] Gruba katılım denemesi ${attempt}/${attempts} başarısız:`, err);
              if (attempt < attempts) {
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
              }
            }
          }

          throw lastError;
        };

        // ✅ Gruba katılma onConnected içinde: reconnect sonrası sunucuda grup
        // üyeliği sıfırlanır. Tekrar katılmadan "Connected" göstermek yanıltıcı
        // olurdu: ikon mavi yanarken tek bir sipariş bildirimi bile gelmez.
        // joinGroups hata atarsa yardımcı bağlantıyı kapatıp yeniden kurar;
        // gruba katılamamış bir bağlantıyı açık bırakmak sahada yaşanan
        // "bağlı görünüyor ama sipariş gelmiyor" durumunu üretiyordu.
        let isFirstConnect = true;

        stopConnection = startResilientConnection(connection, {
          label: 'SignalR',
          onStatusChange: setStatus,
          onConnected: async () => {
            await joinGroups();

            if (isFirstConnect) {
              isFirstConnect = false;
              return;
            }

            // ✅ Kopma süresince kaçırılan siparişleri getir.
            try {
              console.log('🔄 [SignalR] Reconnect sonrası veriler yenileniyor...');
              await fetchMasaSiparisleri();
              await fetchGetirOrders();
              await fetchMigrosOrders();
              await fetchYemeksepetiOrders();
              console.log('[SignalR] Reconnect sonrasi veriler yenilendi');
            } catch (refreshError) {
              console.error('❌ [SignalR] Reconnect sonrası veri yenileme hatası:', refreshError);
            }
          },
        });
      } catch (error) {
        console.error('❌ [SignalR] Bağlantı hatası:', error);
        setStatus('Error');
      }
    };
    
    setupConnection();
    
    // Cleanup
    return () => {
      console.log('🔌 [SignalR] Bağlantı kapatılıyor...');
      // ŞUBE İZOLASYONU: ayrıca LeaveBranchGroup çağırmak gerekmiyor. Bağlantı
      // kapanınca sunucu onu tüm gruplarından çıkarır ve OrdersHub.LeaveBranchGroup
      // da yalnızca RemoveFromGroupAsync yapıyor. Kapanmayı beklemeden ayrılmaya
      // çalışmak, bu arada kopan bağlantının yeniden kurulup yetim kalmasına
      // yol açabiliyordu.
      stopConnection?.();
    };
  }, [companyId, branchId, getOrderSource, isQrOrderForThisBranch, allowedQrMenuIdsLoaded, onNewOrder, onOrderUpdated, onOrderDeleted, onGetirOrderCancelled, onGetirTokenError]);
  
  return {
    status,
    qrMenuOrders,
    ecommerceOrders,
    kioskOrders,
    getirOrders,
    migrosOrders,
    setQrMenuOrders,
    setEcommerceOrders,
    setKioskOrders,
    setGetirOrders,
    setMigrosOrders,
    allowedQrMenuIdsLoaded,
    isQrOrderForThisBranch,
    loadAllowedQrMenuIds,
    fetchMasaSiparisleri,
    fetchGetirOrders,
    fetchMigrosOrders,
    updateMasaSiparisiStatus,
    updateGetirOrderStatus,
    updateMigrosOrderStatus,
    syncGetirOrderStatuses,
    pollMigrosOrders,
    getirRestaurantOpen,
    migrosRestaurantOpen,
    getirRestaurantOpenMap,
    migrosRestaurantOpenMap,
    yemeksepetiVendorOpenMap,
    yemeksepetiOrders,
    setYemeksepetiOrders,
    fetchYemeksepetiOrders,
    updateYemeksepetiOrderStatus,
    yemeksepetiVendorOpen,
    checkYemeksepetiVendorStatus,
    openYemeksepetiVendor,
    closeYemeksepetiVendor,
    checkGetirRestaurantStatus,
    checkMigrosRestaurantStatus,
    openGetirRestaurant,
    closeGetirRestaurant,
    openMigrosRestaurant,
    closeMigrosRestaurant,
  };
};

export default useSignalR;
